import { setGADataLayer } from 'analytics'
import { ReactNode, SyntheticEvent, useContext } from 'react'
import UserContext from 'shared/contexts/user'
import CreationTool from 'site/creation-tools/types/creation-tool'
import styled from 'styled-components'
import { MAX_FILE_COUNT } from '../../../../constants'
import { CreationToolsContext } from '../../../../manager'
import CreationToolError from '../../../../types/error'
import CreationToolOutputType from '../../../../types/output-type'
import CreationToolWelcomeButton from '../../../../types/welcome-button'
import getUnvalidatedSource from '../../../../utils/get-unvalidated-source'
import { validateSourceByInput } from '../../../../utils/validate-source'
import DropZone from '../../../drop-zone'
import { CreationToolsWelcomeContext } from './../../context'

const Container = styled(DropZone)`
    input[type='file'] {
        cursor: pointer;
        height: 100%;
        left: 0;
        margin: 0;
        opacity: 0;
        padding: 0;
        position: absolute;
        top: 0;
        width: 100%;
    }
`

type Props = {
    children?: ReactNode
    className?: string
    button: CreationToolWelcomeButton
    mimeTypesOverride?: string[]
}

const CreationToolsWelcomeFileButton = ({ children, className, button, mimeTypesOverride }: Props) => {
    const { tool, setFiles, setSources, sources } = useContext(CreationToolsContext)
    const { setError, setErrorSource, setIsLoading } = useContext(CreationToolsWelcomeContext)
    const { user } = useContext(UserContext)

    // for the welcome screen we want this output type
    // but for add more we'll want the context's
    const outputType = CreationToolWelcomeButton.outputType(button)
    const mimeTypes = mimeTypesOverride || CreationToolWelcomeButton.mimeTypes(button, user.isStaff)
    const gradient = CreationTool.gradient(tool)

    const onFileInput = async (e: SyntheticEvent<HTMLInputElement, Event>) => {
        const { files } = e.currentTarget
        if (!files || !files.length) return

        const buttonTitle = CreationToolWelcomeButton.title(button)
        const file = files?.[0]
        const fileExtension = file?.name.split('.').pop()
        const isGifMaker = tool === CreationTool.GifMaker

        setGADataLayer({
            event: 'file_selection_open',
            options: {
                options: {
                    form: {
                        formName: 'choose file',
                    },
                },
                content: {
                    contentFormat: buttonTitle,
                    contentType: fileExtension,
                },
            },
        })

        try {
            // check if too many files
            if (files.length > MAX_FILE_COUNT) {
                setError(CreationToolError.TooManyFiles)
                throw new Error('too many files')
            }

            if (button === CreationToolWelcomeButton.UploadAddMore) {
                const newSources = Array.from(files).map(getUnvalidatedSource)
                setSources([...sources, ...newSources])
            } else {
                if (files.length === 1) {
                    const file = files[0]

                    setIsLoading(true)

                    try {
                        const requiresFrameProcessing = isGifMaker && button === CreationToolWelcomeButton.Sticker
                        // if the file will be processed into an array of frames, we don't need
                        // to worry about validating audio or transparency. this will automatically
                        // be accounted for via the tool's creation steps. (i.e. Cut Out)
                        const validatedFile = await validateSourceByInput(file, mimeTypes, {
                            requiresAudio: !requiresFrameProcessing && CreationToolOutputType.requiresAudio(outputType),
                            requiresTransparency:
                                !requiresFrameProcessing && CreationToolOutputType.requiresTransparency(outputType),
                        })
                        setFiles([validatedFile], outputType)
                    } catch (error) {
                        setError(error as CreationToolError)
                        setErrorSource(file)
                        throw Error(CreationToolError.gaLabel(error as CreationToolError, file))
                    }

                    setIsLoading(false)
                } else {
                    const newSources = Array.from(files).map(getUnvalidatedSource)
                    setFiles(newSources, outputType)
                }
            }

            setGADataLayer({
                event: 'file_selection_success',
                options: {
                    form: {
                        formName: 'choose file',
                    },
                    content: {
                        contentFormat: buttonTitle,
                        contentType: fileExtension,
                    },
                },
            })
        } catch (err) {
            setGADataLayer({
                event: 'file_selection_error',
                options: {
                    form: {
                        formName: 'choose file',
                        formError: err,
                    },
                    content: {
                        contentFormat: buttonTitle,
                        contentType: fileExtension,
                    },
                },
            })
        }
    }

    return (
        <Container
            {...{
                className,
                gradient,
            }}
        >
            <>
                {children}
                <input
                    type="file"
                    accept={mimeTypes.join(',')}
                    multiple={CreationTool.supportsMultipleFiles(tool)}
                    onInput={onFileInput}
                />
            </>
        </Container>
    )
}

export default CreationToolsWelcomeFileButton
